import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, TextField, Checkbox, IconButton, Button, Stack, Grid, Avatar, } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { FaFileExcel } from 'react-icons/fa';
import DetailCard from '../components/DetailCard'; // Adjust the path if needed
import LoadingOverlay from '../components/LoadingOverlay';

// const path = `http://103.251.218.254:91/api/api`

const UnitDetail = ({ loggedInUser, companyData, connectionString }) => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [UnitSearchTerm, setUnitSearchTerm] = useState('');
    const [resetFlag, setResetFlag] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(5);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [mode, setMode] = useState('search');
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const path = `${connectionString}/api`;

    useEffect(() => {
        fetchData();
        fetchTotalCount();
    }, [resetFlag, offset, limit, mode]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            let apiUrl = `${path}/unit/search?mode=${mode}&offset=${offset}&limit=${limit}`;
            if (mode === 'search') {
                if (resetFlag === 1) {
                    setUnitSearchTerm('');
                    setOffset(0);
                    setResetFlag(0);
                    setSelectedRows([]);
                    setSelectAll(false);
                } else {
                    apiUrl += `&unit=${UnitSearchTerm}`;
                }

                const response = await axios.get(apiUrl);
                if (Array.isArray(response.data)) {
                    setData(offset === 0 ? response.data : [...data, ...response.data]);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTotalCount = async () => {
        try {
            const response = await axios.get(`${path}/unit/count`);
            setTotalCount(response.data);
        } catch (error) {
            console.error('Error fetching total count:', error);
        }
    };

    const funExportData = () => {
        axios({
            url: `${path}/unit/search?mode=export`,
            method: 'GET',
            responseType: 'blob',
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'exportedFileUnit.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setMode('search');
        }).catch(error => {
            console.error("Excel download error!", error);
        });
    };

    const funSearch = () => {
        setOffset(0);
        setMode('search');
        fetchData();
        fetchTotalCount();
    };

    const funReset = () => {
        setResetFlag(1);
        setMode('search');
    };

    const funShowMore = () => {
        if (data.length < totalCount) {
            setOffset(prev => prev + limit);
        }
    }

    const funSelectAll = () => {
        setSelectAll(!selectAll);
        setSelectedRows(!selectAll ? data.map(item => item.unitId) : []);
    };

    const funRowSelect = (id) => { setSelectedRows(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]); };

    const funDeleteSelectedRows = async () => {
        try {
            await axios.post(`${path}/unit/Delete`, { objAryId: selectedRows });
            funReset();
        } catch (error) {
            console.error('Delete error:', error);
        }
    };

    const toggleVisibility = () => { setIsVisible(prev => !prev); };

    const handleCancel = () => {
        toggleVisibility();
        funReset();
    };

    const handleFilter = () => {
        toggleVisibility();
        funSearch();
    };

    return (
        <>
            <LoadingOverlay isLoading={isLoading} message="Loading data..." />
            <Box p={2} display="flex" flexDirection="column" gap={2}>
            {/* Top Bar */}
            <Paper sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton color="primary" onClick={toggleVisibility}>
                        <FilterListIcon />
                    </IconButton>
                    <IconButton component={Link} to="/main/unitMaster" color="primary">
                        <AddIcon />
                    </IconButton>
                    <Checkbox checked={selectAll} onChange={funSelectAll} />
                    <IconButton color="error" onClick={funDeleteSelectedRows} disabled={selectedRows.length === 0}>
                        <DeleteIcon />
                    </IconButton>
                    <IconButton onClick={funExportData} color="success">
                        <FaFileExcel />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary">{data.length}/{totalCount}</Typography>
            </Paper>

            {/* Filter Section */}
            {isVisible && (
                <Box display="flex" justifyContent="center">
                    <Paper sx={{ p: 4, borderRadius: 3, bgcolor: '#f1f5f9', border: '1px solid #e5e7eb' }}>
                        <Typography variant="h6" fontWeight={600} color="#1f2937" mb={2}>
                            Filter Units
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Unit"
                                    size="small"
                                    value={UnitSearchTerm}
                                    onChange={(e) => setUnitSearchTerm(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={2} justifyContent="center">
                                    <Button variant="contained" onClick={handleFilter}>Search</Button>
                                    <Button variant="outlined" color="error" onClick={handleCancel}>Cancel</Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>
            )}

            {/* Unit Cards */}
            {data.map((item, index) => (
                <DetailCard
                    key={item.unitId}
                    id={item.unitId}
                    title={item.unit}
                    avatarText={item.unit?.charAt(0)}
                    subtitleList={[]} // No subtitles for unit
                    selected={selectedRows.includes(item.unitId)}
                    onSelect={funRowSelect}
                    onEdit={(id) => navigate(`/main/editUnitMaster/${id}`)}
                    backgroundColor="#f8fafc"
                />
            ))}

            {/* Show More Button */}
            {data.length < totalCount && (
                <Box textAlign="center" mt={1}>
                    <Button variant="outlined" onClick={funShowMore}>Show More</Button>
                </Box>
            )}
        </Box>
        </>
    );
};

export default UnitDetail;